import React from "react";
import { createRoot } from "react-dom/client";
import { Controller } from "@hotwired/stimulus";
import Sorter from "../../components/shared/tables/Sorter";

// Usage see: app/javascript/controllers/administration/pages/sortable_table_controller.jsx
// Connects to data-controller="shared--sortable-table"
export default class extends Controller {
  static targets = ["sorterContainer", "tableBody"];
  static values = {
    sortAttribute: String,
    sortDirection: { type: Number, default: 1 },
  };

  roots = new Map();
  sortCache = new Map();

  connect() {
    this.renderSorters();
  }

  disconnect() {
    this.roots.forEach((root) => {
      root.unmount();
    });
    this.roots.clear();
    this.sortCache.clear();
  }

  handleSort = ([attribute, direction]) => {
    if (
      this.sortAttributeValue !== attribute ||
      this.sortDirectionValue !== direction
    ) {
      this.sortAttributeValue = attribute;
      this.sortDirectionValue = direction;
      this.sortTable();
      this.renderSorters();
    }
  };

  renderSorters() {
    this.sorterContainerTargets.forEach((container) => {
      const attribute = container.dataset.attribute;
      const label = container.dataset.label;

      let root;
      if (!this.roots.has(container)) {
        root = createRoot(container);
        this.roots.set(container, root);
      } else {
        root = this.roots.get(container);
      }

      root.render(
        <Sorter
          attribute={attribute}
          label={label}
          sortBy={[this.sortAttributeValue, this.sortDirectionValue]}
          handleSort={this.handleSort}
        />,
      );
    });
  }

  sortTable() {
    const rows = Array.from(this.tableBodyTarget.querySelectorAll("tr"));
    const sortedRows = this.sortRows(rows);

    const fragment = document.createDocumentFragment();
    sortedRows.forEach((row) => fragment.appendChild(row));

    this.tableBodyTarget.innerHTML = "";
    this.tableBodyTarget.appendChild(fragment);
  }

  sortRows(rows) {
    const attribute = this.sortAttributeValue;
    const direction = this.sortDirectionValue;

    const cacheKey = `${attribute}-${direction}`;

    // Return cached result if available
    if (this.sortCache.has(cacheKey)) {
      return [...this.sortCache.get(cacheKey)];
    }

    const columnIndex = this.getColumnIndex(attribute);

    const sortedRows = [...rows].sort((rowA, rowB) => {
      const cellA = rowA.querySelectorAll("td")[columnIndex];
      const cellB = rowB.querySelectorAll("td")[columnIndex];

      if (!cellA || !cellB) return 0;

      if (attribute === "created_at") {
        const sortValueA = cellA.dataset.sortValue;
        const sortValueB = cellB.dataset.sortValue;

        if (sortValueA && sortValueB) {
          // Perform numeric comparison with timestamps
          return direction * (parseInt(sortValueA) - parseInt(sortValueB));
        }
      }

      // Fallback to text content for other column types
      const valueA = cellA.textContent.trim();
      const valueB = cellB.textContent.trim();

      // Numeric handling for number columns
      if (!isNaN(parseFloat(valueA)) && !isNaN(parseFloat(valueB))) {
        return direction * (parseFloat(valueA) - parseFloat(valueB));
      }

      return (
        direction *
        valueA.localeCompare(valueB, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
    });
    this.sortCache.set(cacheKey, [...sortedRows]);

    return sortedRows;
  }

  // Must be overridden by derived controllers
  getColumnIndex(attribute) {
    console.error(
      `Error: getColumnIndex() method must be implemented in the derived controller. Called with attribute "${attribute}".`,
    );
    return 0;
  }
}
